import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const EFFECTIVE_DATE = 'May 5, 2026';

function Section({ title, children }) {
  return (
    <div className="mb-10">
      <h2 className="font-display text-xl text-ink mb-3">{title}</h2>
      <div className="space-y-3 text-slate2 leading-relaxed">{children}</div>
    </div>
  );
}

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = 'Privacy Policy — Learning Outcomes Academy';
    return () => { document.title = 'Learning Outcomes Academy'; };
  }, []);

  return (
    <div className="w-full bg-bone">
      <div className="container-page pt-16 pb-12 max-w-3xl">
        <div className="text-[11px] tracking-[0.28em] uppercase text-brand-aubergine mb-4">
          Legal
        </div>
        <h1 className="font-display text-4xl text-ink leading-tight mb-2">
          Privacy Policy
        </h1>
        <p className="text-slate2 text-sm mb-12">
          Effective date: {EFFECTIVE_DATE}
        </p>

        <Section title="Who we are">
          <p>
            Learning Outcomes Academy ("we", "us", "our") is operated from Oshawa, Ontario, Canada.
            We offer online professional development courses available to learners worldwide.
            Questions about this policy can be directed to us via our{' '}
            <a href="/contact" className="text-ink underline underline-offset-2 hover:text-brand-deep">
              contact form
            </a>.
          </p>
        </Section>

        <Section title="What personal information we collect">
          <p>We collect only what we need to provide our service:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong className="text-ink font-medium">Email address</strong> — used to create your account, send your sign-in link, and deliver course-related communications.</li>
            <li><strong className="text-ink font-medium">Name</strong> — collected at checkout (as entered for billing) and used on your certificate of completion.</li>
            <li><strong className="text-ink font-medium">Payment information</strong> — processed entirely by Stripe. We never see or store your card number, expiry, or CVV.</li>
            <li><strong className="text-ink font-medium">Course progress</strong> — lessons completed, quiz results, and final assessment attempts, stored to provide your learning experience and certificate.</li>
            <li><strong className="text-ink font-medium">Session data</strong> — a secure session token stored in a cookie to keep you signed in, and your browser's user-agent string to help you manage active sessions.</li>
          </ul>
          <p>
            We do not collect your location, phone number, or any government identification. We do not
            use tracking pixels, advertising cookies, or behavioural profiling.
          </p>
        </Section>

        <Section title="Why we collect it">
          <p>We use your information to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Create and manage your account</li>
            <li>Deliver your sign-in link and course access</li>
            <li>Record your progress and issue your certificate of completion</li>
            <li>Process your payment and send a purchase confirmation</li>
            <li>Respond to support requests</li>
            <li>Comply with applicable law</li>
          </ul>
          <p>
            We do not sell your personal information to anyone, ever.
          </p>
        </Section>

        <Section title="Third-party services we use">
          <p>
            To deliver our service, we share limited information with the following trusted providers.
            Each is bound by its own privacy policy and applicable data protection law.
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong className="text-ink font-medium">Stripe</strong> — payment processing. Stripe handles all card data under PCI-DSS compliance.</li>
            <li><strong className="text-ink font-medium">Resend</strong> — transactional email delivery for sign-in links and purchase confirmations.</li>
            <li><strong className="text-ink font-medium">Cloudflare Stream</strong> — secure video hosting for course lessons.</li>
          </ul>
          <p>
            By using our service, you acknowledge that your information may be processed in Canada
            and other countries where these providers operate.
          </p>
        </Section>

        <Section title="Cookies and session storage">
          <p>
            We use a single session cookie to keep you signed in. This cookie is strictly necessary
            for the platform to function and does not track you across other websites. We do not use
            advertising cookies, analytics cookies, or any third-party tracking scripts.
          </p>
        </Section>

        <Section title="How long we keep your information">
          <p>
            We retain your account information, course progress, and purchase records for as long as
            your account is active. If you request deletion of your account, we will remove your
            personal information within 30 days, except where we are required to keep certain records
            for legal or tax compliance purposes (typically up to 7 years for financial records under
            Canadian law).
          </p>
        </Section>

        <Section title="Your rights">
          <p>
            Depending on where you are located, you may have rights to access, correct, export, or
            delete your personal information. These rights are recognized under Canadian privacy law
            (PIPEDA) and, where applicable, the European GDPR and other regional frameworks.
          </p>
          <p>
            To exercise any of these rights, use our{' '}
            <a href="/contact" className="text-ink underline underline-offset-2 hover:text-brand-deep">
              contact form
            </a>{' '}
            and we will respond within 30 days.
          </p>
        </Section>

        <Section title="Children's privacy">
          <p>
            Our courses are intended for adults. We do not knowingly collect personal information
            from anyone under the age of 16. If you believe a minor has provided us with personal
            information, please contact us and we will delete it promptly.
          </p>
        </Section>

        <Section title="Changes to this policy">
          <p>
            If we make material changes to this Privacy Policy, we will update the effective date
            above and, where appropriate, notify you by email. Continued use of our platform after
            changes take effect constitutes your acceptance of the updated policy.
          </p>
        </Section>

        <Section title="Contact us">
          <p>
            For any privacy-related questions or requests, please use our{' '}
            <Link to="/contact" className="text-ink underline underline-offset-2 hover:text-brand-deep">
              contact form
            </Link>
            . We will respond within 30 days.
          </p>
        </Section>
      </div>
    </div>
  );
}
